import { useState } from "react";
import { Helmet } from "react-helmet";
import { js_beautify } from "js-beautify";
import BackButton from "../components/BackButton";

export default function JavaScriptFormatter() {
  const [input, setInput] = useState("");
  const [formatted, setFormatted] = useState("");
  const [minified, setMinified] = useState("");
  const [error, setError] = useState("");
  const [runOutput, setRunOutput] = useState("");

  const formatJS = () => {
    try {
      const beautified = js_beautify(input, {
        indent_size: 2,
        space_in_empty_paren: true,
      });
      setFormatted(beautified);
      setMinified("");
      setError("");
      setRunOutput(""); // clear previous
    } catch (err) {
      setError("Error formatting JavaScript: " + err.message);
    }
  };

  const minifyJS = () => {
    try {
      const minifiedCode = input
        .replace(/\/\/.*$/gm, "") // remove single-line comments
        .replace(/\s+/g, " ") // collapse whitespace
        .replace(/\s*([=;{}(),:+\-*/<>])\s*/g, "$1") // trim around operators
        .trim();
      setMinified(minifiedCode);
      setFormatted("");
      setError("");
      setRunOutput(""); // clear previous
    } catch (err) {
      setError("Error minifying JavaScript: " + err.message);
    }
  };

  const clearInput = () => {
    setInput("");
    setFormatted("");
    setMinified("");
    setError("");
    setRunOutput("");
  };

  const resetOutput = () => {
    setFormatted("");
    setMinified("");
    setError("");
    setRunOutput("");
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert("Copied to clipboard!");
  };

  const downloadFile = (text, filename) => {
    const blob = new Blob([text], { type: "text/javascript" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const runCode = (code) => {
    setRunOutput(code);
  };

  return (
    <>
      <Helmet>
           <title>JavaScript Minifier & Formatter Online - Free JS Tool</title>
           <meta
             name="description"
             content="Minify or beautify your JavaScript code online with this free tool. Instantly compress, clean, and format JS files for better readability and performance."
           />
           <meta
             name="keywords"
             content="javascript minifier, js formatter, online js minifier, free js beautifier, compress javascript, format js code"
           />
           <meta name="robots" content="index, follow" />
           <link
             rel="canonical"
             href="https://freelity.com/javascript-formatter"
           />
      </Helmet>

      <section className="sec-free-tools">            
        <div className="container">
          <BackButton />
            <h2>JavaScript Minifier & Formatter</h2>

            <textarea
              rows="10"
              className="form-control mb-3"
              placeholder="Paste your JavaScript code here..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
            />

            <div className="mb-3">
              <button className="btn btn-primary me-2" onClick={formatJS}>
                Format JS
              </button>
              <button className="btn btn-success me-2" onClick={minifyJS}>
                Minify JS
              </button>
              <button className="btn btn-danger me-2" onClick={clearInput}>
                Clear Input
              </button>
              <button className="btn btn-secondary" onClick={resetOutput}>
                Reset Output
              </button>
            </div>

            {error && <div className="alert alert-danger mt-3">{error}</div>}

            {formatted && (
              <>
                <h5 className="mt-4">Formatted Code:</h5>
                <textarea
                  rows="10"
                  className="form-control mb-2"
                  value={formatted}
                  readOnly
                />
                <button
                  className="btn btn-outline-primary me-2 mb-2 bigsize"
                  onClick={() => copyToClipboard(formatted)}
                >
                  Copy Formatted Code
                </button>
                <button
                  className="btn btn-outline-dark me-2 mb-2 bigsize"
                  onClick={() => downloadFile(formatted, "formatted.js")}
                >
                  Download Formatted JS
                </button>
                <button
                  className="btn btn-outline-warning mb-2 bigsize"
                  onClick={() => runCode(formatted)}
                >
                  Run Formatted Code
                </button>
              </>
            )}

            {minified && (
              <>
                <h5 className="mt-4">Minified Code:</h5>
                <textarea
                  rows="6"
                  className="form-control mb-2"
                  value={minified}
                  readOnly
                />
                <button
                  className="btn btn-outline-success me-2 mb-2 bigsize"
                  onClick={() => copyToClipboard(minified)}
                >
                  Copy Minified Code
                </button>
                <button
                  className="btn btn-outline-dark me-2 mb-2 bigsize"
                  onClick={() => downloadFile(minified, "minified.js")}
                >
                  Download Minified JS
                </button>
                <button
                  className="btn btn-outline-warning mb-2 bigsize"
                  onClick={() => runCode(minified)}
                >
                  Run Minified Code
                </button>
              </>
            )}

            {runOutput && (
              <div className="mt-5">
                <h5>Live Output (Run Result):</h5>
                <iframe
                  title="Live Output"
                  sandbox="allow-scripts"
                  style={{ width: "100%", height: "200px", border: "1px solid #ccc" }}
                  srcDoc={`<html><body><script>${runOutput}</script></body></html>`}
                />
              </div>
            )}
        </div>
      </section>
    </>
  );
}
